/**
 * routeAccess.ts — Single source of truth pemetaan rute → izin menu.
 *
 * Digunakan oleh PortalGuruAccessGate untuk menegakkan Broken Access Control
 * (forced browsing) di level routing, SAMA dengan yang ditampilkan sidebar
 * (PortalGuruDashboard MENU_ITEMS). Super Admin selalu diizinkan (bypass).
 *
 * Aturan (rule):
 *   - `prefix`      : awalan path yang dicocokkan (longest-prefix match).
 *   - `menuIds`     : user CUKUP punya SALAH SATU menu ID (semantik OR).
 *   - `superAdminOnly`: bila true, HANYA super admin yang boleh (default-deny).
 *
 * Rute `/portal-guru/*` yang TIDAK cocok dengan rule apa pun dianggap
 * "akses dasar" → butuh menu `portal-guru` (perilaku umum bagi halaman profil,
 * kinerja, berita, dsb). Rute di bawah `/admin` yang tidak cocok → super admin only.
 */

export interface RouteAccessRule {
  prefix: string;
  menuIds?: string[];
  superAdminOnly?: boolean;
}

export const BASIC_ACCESS_MENU_ID = 'portal-guru';

/** Menu yang membuka /kiosk (rute di luar PortalGuruAccessGate). */
export const KIOSK_MENU_IDS = ['pg-kiosk-presensi', 'pg-menu-presensi-kegiatan'];

/**
 * Daftar rute yang HANYA boleh diakses Super Admin (default-deny bagi role lain).
 * Kecuali ada rule lain yang lebih spesifik (longest-prefix) yang mengizinkan
 * role tertentu (mis. `/portal-guru/admin/kelola-berita` untuk MIAS Media).
 */
export const ROUTE_ACCESS_RULES: RouteAccessRule[] = [
  // ── SUPER ADMIN ONLY ────────────────────────────────────────────────
  { prefix: '/portal-guru/admin/inbox', superAdminOnly: true },
  { prefix: '/portal-guru/admin/migrasi-data', superAdminOnly: true },
  { prefix: '/portal-guru/admin/versi-aplikasi', superAdminOnly: true },
  { prefix: '/portal-guru/admin/presensi-masuk', superAdminOnly: true },
  { prefix: '/portal-guru/admin/kompensasi-telat', superAdminOnly: true },
  { prefix: '/portal-guru/admin/presensi-pulang', superAdminOnly: true },
  { prefix: '/portal-guru/admin/lokasi-presensi', superAdminOnly: true },
  { prefix: '/portal-guru/admin/grup-presensi', superAdminOnly: true },
  { prefix: '/portal-guru/admin/kelola-berkas', superAdminOnly: true },
  { prefix: '/portal-guru/admin/kelola-petugas-izin', superAdminOnly: true },
  { prefix: '/portal-guru/admin/pengaturan', superAdminOnly: true },
  { prefix: '/portal-guru/admin/kartu-santri', superAdminOnly: true },
  { prefix: '/portal-guru/admin/sertifikat', superAdminOnly: true },

  // ── MENU UMUM ───────────────────────────────────────────────────────
  { prefix: '/portal-guru/izin-staf', menuIds: ['pg-menu-umum'] },
  { prefix: '/portal-guru/laporan-kerja', menuIds: ['pg-menu-umum'] },
  { prefix: '/portal-guru/menu-umum', menuIds: ['pg-menu-umum'] },
  { prefix: '/portal-guru/kaldik-umum', menuIds: ['pg-menu-umum'] },
  { prefix: '/portal-guru/prestasi-santri/list', menuIds: ['pg-menu-umum'] },
  { prefix: '/portal-guru/live-streaming', menuIds: ['pg-menu-umum'] },
  { prefix: '/portal-guru/tahfidz/hafalan-santri', menuIds: ['pg-menu-umum'] },

  // ── MUDIR ───────────────────────────────────────────────────────────
  { prefix: '/portal-guru/admin/cari-santri', menuIds: ['pg-mudir', 'pg-kepala-divisi', 'pg-departemen-pendidikan', 'pg-mias-media'] },
  { prefix: '/portal-guru/mudir/pantau-kelas', menuIds: ['pg-mudir', 'pg-departemen-pendidikan'] },
  { prefix: '/portal-guru/mudir', menuIds: ['pg-mudir'] },
  { prefix: '/portal-guru/cctv', menuIds: ['pg-mudir', 'pg-kepala-divisi'] },

  // ── AKADEMIK ────────────────────────────────────────────────────────
  { prefix: '/portal-guru/akademik', menuIds: ['pg-akademik'] },

  // ── WAKA KURIKULUM (banyak yang dipakai bersama) ───────────────────
  { prefix: '/portal-guru/waka-kurikulum/kelola-kelas', menuIds: ['pg-waka-kurikulum', 'pg-tata-usaha'] },
  { prefix: '/portal-guru/waka-kurikulum/input-kaldik', menuIds: ['pg-waka-kurikulum', 'pg-departemen-pendidikan', 'pg-kesantrian'] },
  { prefix: '/portal-guru/waka-kurikulum/dasbor-santri', menuIds: ['pg-waka-kurikulum', 'pg-mudir', 'pg-departemen-pendidikan', 'pg-kepala-divisi', 'pg-waka-kesiswaan'] },
  { prefix: '/portal-guru/waka-kurikulum/dasbor-staf', menuIds: ['pg-waka-kurikulum', 'pg-mudir', 'pg-departemen-pendidikan', 'pg-kepala-divisi', 'pg-waka-kesiswaan'] },
  { prefix: '/portal-guru/waka-kurikulum/kegiatan-pengganti', menuIds: ['pg-waka-kurikulum', 'pg-tata-usaha'] },
  { prefix: '/portal-guru/waka-kurikulum/kelola-mutabaah', menuIds: ['pg-waka-kurikulum'] },
  { prefix: '/portal-guru/waka-kurikulum', menuIds: ['pg-waka-kurikulum'] },

  // ── TAHFIDZ ─────────────────────────────────────────────────────────
  { prefix: '/portal-guru/tahfidz/kelola-halaqoh', menuIds: ['pg-koordinator-tahfidz'] },
  { prefix: '/portal-guru/tahfidz/kelola-jam', menuIds: ['pg-koordinator-tahfidz'] },
  { prefix: '/portal-guru/tahfidz/migrasi-jam', menuIds: ['pg-koordinator-tahfidz'] },
  { prefix: '/portal-guru/tahfidz/pantau', menuIds: ['pg-koordinator-tahfidz', 'pg-mudir', 'pg-kepala-divisi', 'pg-departemen-pendidikan'] },
  { prefix: '/portal-guru/tahfidz/hafalan-awal', menuIds: ['pg-tahfidz', 'pg-koordinator-tahfidz'] },
  { prefix: '/portal-guru/tahfidz/sertifikat', menuIds: ['pg-koordinator-tahfidz'] },
  { prefix: '/portal-guru/tahfidz/rekap', menuIds: ['pg-koordinator-tahfidz', 'pg-tahfidz'] },
  { prefix: '/portal-guru/tahfidz', menuIds: ['pg-tahfidz', 'pg-koordinator-tahfidz'] },

  // ── MUROBBI ─────────────────────────────────────────────────────────
  { prefix: '/portal-guru/murobbi', menuIds: ['pg-murobbi'] },
  { prefix: '/portal-guru/data-santri-wali', menuIds: ['pg-murobbi', 'pg-wali-kelas'] },

  // ── WALI KELAS ──────────────────────────────────────────────────────
  { prefix: '/portal-guru/wali-kelas/monitor-nilai', menuIds: ['pg-wali-kelas', 'pg-kepala-sekolah'] },
  { prefix: '/portal-guru/wali-kelas', menuIds: ['pg-wali-kelas'] },

  // ── KEPALA DIVISI ───────────────────────────────────────────────────
  { prefix: '/portal-guru/kepala-divisi/kelola-pertemuan', menuIds: ['pg-mudir', 'pg-kepala-divisi', 'pg-departemen-pendidikan'] },
  { prefix: '/portal-guru/kepala-divisi/laporan-kinerja', menuIds: ['pg-kepala-divisi', 'pg-mudir'] },
  { prefix: '/portal-guru/kepala-divisi', menuIds: ['pg-kepala-divisi'] },
  { prefix: '/portal-guru/kelola-tugas-staf', menuIds: ['pg-waka-kurikulum', 'pg-kepala-divisi'] },

  // ── DEPARTEMEN PENDIDIKAN ───────────────────────────────────────────
  { prefix: '/portal-guru/departemen-pendidikan', menuIds: ['pg-departemen-pendidikan'] },

  // ── WAKA KESISWAAN ──────────────────────────────────────────────────
  { prefix: '/portal-guru/waka-kesiswaan', menuIds: ['pg-waka-kesiswaan'] },

  // ── KESANTRIAN ──────────────────────────────────────────────────────
  { prefix: '/portal-guru/kesantrian', menuIds: ['pg-kesantrian'] },

  // ── TATA USAHA ──────────────────────────────────────────────────────
  { prefix: '/portal-guru/tu', menuIds: ['pg-tata-usaha'] },
  { prefix: '/portal-guru/admin/data-santri', menuIds: ['pg-tata-usaha'] },
  { prefix: '/portal-guru/admin/cetak-kartu-santri', menuIds: ['pg-tata-usaha'] },

  // ── SEKRETARIAT ─────────────────────────────────────────────────────
  { prefix: '/sekretariat', menuIds: ['pg-sekretariat'] },

  // ── PERIZINAN / RESEPSIONIS / KESEHATAN ────────────────────────────
  { prefix: '/portal-guru/perizinan/santri', menuIds: ['pg-bagian-perizinan'] },
  { prefix: '/portal-guru/resepsionis', menuIds: ['pg-bagian-resepsionis'] },
  { prefix: '/portal-guru/dasbor-perizinan', menuIds: ['pg-mudir', 'pg-murobbi', 'pg-wali-kelas', 'pg-kepala-divisi', 'pg-departemen-pendidikan'] },
  { prefix: '/portal-guru/dasbor-klinik', menuIds: ['pg-mudir', 'pg-murobbi', 'pg-wali-kelas', 'pg-kepala-divisi', 'pg-departemen-pendidikan'] },
  { prefix: '/klinik-dashboard', menuIds: ['pg-bagian-kesehatan'] },
  { prefix: '/select-view/infoscreen', menuIds: ['pg-bagian-perizinan'] },

  // ── KEUANGAN ────────────────────────────────────────────────────────
  { prefix: '/portal-guru/keuangan', menuIds: ['pg-keuangan'] },

  // ── TAKMIR ──────────────────────────────────────────────────────────
  { prefix: '/portal-guru/takmir', menuIds: ['pg-takmir'] },

  // ── STAF TARBIYAH / LUGHOH / PENJADWALAN ───────────────────────────
  { prefix: '/portal-guru/tarbiyah', menuIds: ['pg-staf-tarbiyah'] },
  { prefix: '/portal-guru/lughoh', menuIds: ['pg-staf-lughoh'] },
  { prefix: '/portal-guru/staf-penjadwalan', menuIds: ['pg-staf-penjadwalan'] },

  // ── EKSTRAKURIKULER ─────────────────────────────────────────────────
  { prefix: '/portal-guru/ekskul/kelola', menuIds: ['pg-waka-kesiswaan', 'pg-ekskul'] },
  { prefix: '/portal-guru/ekskul/monitor', menuIds: ['pg-waka-kesiswaan', 'pg-ekskul'] },
  { prefix: '/portal-guru/ekskul', menuIds: ['pg-ekskul', 'pg-waka-kesiswaan'] },

  // ── PRESTASI / KELULUSAN ────────────────────────────────────────────
  { prefix: '/portal-guru/prestasi-santri/input', menuIds: ['pg-prestasi'] },
  { prefix: '/portal-guru/prestasi-santri', menuIds: ['pg-menu-umum', 'pg-prestasi'] },
  { prefix: '/portal-guru/prestasi', menuIds: ['pg-prestasi'] },

  // ── INFO & PERKEMBANGAN SANTRI ──────────────────────────────────────
  { prefix: '/portal-guru/perkembangan', menuIds: ['pg-murobbi', 'pg-wali-kelas', 'pg-tata-usaha'] },

  // ── KEGIATAN SUMA ───────────────────────────────────────────────────
  { prefix: '/portal-guru/suma/kurikulum', menuIds: ['pg-kurikulum-suma'] },
  { prefix: '/portal-guru/suma', menuIds: ['pg-kegiatan-suma'] },

  // ── PRESENSI KEGIATAN & LIBURAN ─────────────────────────────────────
  { prefix: '/kegiatan-dashboard', menuIds: ['pg-menu-presensi-kegiatan'] },
  { prefix: '/kegiatan-absen', menuIds: ['pg-menu-presensi-kegiatan'] },
  { prefix: '/kegiatan-santri', menuIds: ['pg-menu-presensi-kegiatan'] },
  { prefix: '/absensi-liburan', menuIds: ['pg-menu-presensi-kegiatan'] },
  // Kiosk & Kelola Kegiatan: menu sendiri, dan juga terbuka untuk pemegang "Presensi Kegiatan".
  // /kiosk ada di luar gate Portal Guru; ProtectedRoute-nya memakai KIOSK_MENU_IDS.
  { prefix: '/kiosk', menuIds: KIOSK_MENU_IDS },
  { prefix: '/portal-guru/presensi-kegiatan/kelola-kegiatan', menuIds: ['pg-kelola-kegiatan', 'pg-menu-presensi-kegiatan'] },

  // ── QURBAN ──────────────────────────────────────────────────────────
  { prefix: '/portal-guru/qurban', menuIds: ['pg-qurban'] },

  // ── MIAS MEDIA (bersama Super Admin) ────────────────────────────────
  { prefix: '/portal-guru/admin/kelola-berita', menuIds: ['pg-mias-media'] },
  { prefix: '/portal-guru/admin/hak-akses', menuIds: ['pg-mias-media'] },
  { prefix: '/portal-guru/admin/kelola-bingkai', menuIds: ['pg-mias-media'] },
  { prefix: '/portal-guru/admin/booking-lab', menuIds: ['pg-akademik'] },
  // ── ADMIN PANEL ─────────────────────────────────────────────────────
  { prefix: '/admin', menuIds: ['pg-tata-usaha', 'pg-kesantrian'] },
  // Catch-all area admin (rute lain di bawah /portal-guru/admin → super admin only)
  { prefix: '/portal-guru/admin', superAdminOnly: true },

  // ── PERALATAN ───────────────────────────────────────────────────────
  { prefix: '/portal-guru/peralatan', menuIds: ['pg-peralatan'] },
  { prefix: '/portal-guru/timer', menuIds: ['pg-peralatan'] },

  // ── PENANGANAN INDISIPLINER (dipakai banyak role) ──────────────────
  { prefix: '/portal-guru/penanganan-indisipliner', menuIds: ['pg-mudir', 'pg-tahfidz', 'pg-murobbi', 'pg-wali-kelas', 'pg-kepala-divisi', 'pg-waka-kesiswaan'] },

  // ── MUTABAAH (dipakai murobbi & wali kelas) ────────────────────────
  { prefix: '/portal-guru/mutabaah', menuIds: ['pg-murobbi', 'pg-wali-kelas'] },

  // ── PENUGASAN LUAR (dipakai banyak role) ───────────────────────────
  { prefix: '/portal-guru/penugasan-luar', menuIds: ['pg-waka-kurikulum', 'pg-kepala-divisi', 'pg-waka-kesiswaan', 'pg-tata-usaha'] },
];

/**
 * Mencocokkan pathname terhadap aturan dengan strategi longest-prefix match.
 * Mengembalikan rule yang cocok, atau `null` bila tidak ada (→ pakai default).
 */
export function matchRouteAccess(pathname: string): RouteAccessRule | null {
  let best: RouteAccessRule | null = null;
  let bestLen = -1;

  for (const rule of ROUTE_ACCESS_RULES) {
    if (pathname === rule.prefix || pathname.startsWith(rule.prefix + '/')) {
      if (rule.prefix.length > bestLen) {
        best = rule;
        bestLen = rule.prefix.length;
      }
    }
  }

  return best;
}

/**
 * Apakah pathname termasuk area super admin (`/admin`) yang secara default
 * hanya boleh diakses super admin, kecuali ada rule lebih spesifik.
 */
export function isAdminArea(pathname: string): boolean {
  return pathname === '/admin' || pathname.startsWith('/admin');
}
